import { type AreaData, type AreaListInput, isDefined, isEmpty, isNotEmpty } from '@terros/common'

/** true: the list includes the area. false: it clearly does not. undefined: only the server can tell. */
export type AreaListMatch = boolean | undefined

/**
 * Whether a /area/list v2 query with `input` would return `area`, judged from the area alone.
 * Mirrors the server: `userId` wins over team filters, and archived areas are hidden unless asked for.
 * Downline teams, text search, states, and date ranges need server data, so they give undefined unless another
 * filter already rules the area out. A bounding box is ignored: maps filter to their viewport anyway, so a newly
 * drawn area shows up right away.
 */
export function matchAreaListInput(area: AreaData, input: AreaListInput): AreaListMatch {
  const checks = [matchArchived(area, input), matchAssignee(area, input), matchServerOnlyFilters(input)]
  if (checks.includes(false)) return false
  if (checks.includes(undefined)) return undefined
  return true
}

function matchArchived(area: AreaData, { archived = false }: AreaListInput): AreaListMatch {
  if (archived === 'all') return true
  return (area.isDeleted === true) === archived
}

function matchAssignee(
  area: AreaData,
  { userId, teamId, teamIds = [], downlineTeamIds = [] }: AreaListInput
): AreaListMatch {
  if (isDefined(userId)) return (area.userIds ?? []).includes(userId)
  if (isNotEmpty(downlineTeamIds)) {
    if (isDefined(area.teamId) && downlineTeamIds.includes(area.teamId)) return true
    return undefined
  }
  const directTeamIds = isDefined(teamId) ? [...teamIds, teamId] : teamIds
  if (isEmpty(directTeamIds)) return true
  return isDefined(area.teamId) && directTeamIds.includes(area.teamId)
}

function matchServerOnlyFilters(input: AreaListInput): AreaListMatch {
  const { query, areaStates, createdDate, lastAssignmentDate, expirationDate } = input
  if (isNotEmpty(query?.trim())) return undefined
  if (isNotEmpty(areaStates)) return undefined
  if ([createdDate, lastAssignmentDate, expirationDate].some(isDefined)) return undefined
  return true
}
